// SupplierChips — the retail-provider roster.
//
// Two presentations of the same list:
//
//  - default: a wrapping cloud of Badge chips (centred on request);
//  - marquee: a slow, continuous belt for the landing page — two copies of the
//    list on one translating track, edges masked so names dissolve in and out.
//    The duplicate copy is aria-hidden: a screen reader hears each supplier
//    once, in order, exactly as in the static cloud.
//
// Real catalogue supplier NAMES only — no trademarked logos (unlicensed).

import { Badge } from '@fluxora/ui';

export interface SupplierChipsProps {
  readonly suppliers: readonly string[];
  /** Centre-align the wrapping cloud, so each row balances left-and-right. */
  readonly centered?: boolean;
  /** Render as the auto-scrolling belt instead of the wrapping cloud. */
  readonly marquee?: boolean;
}

function ChipRow({
  suppliers,
  hidden = false,
}: {
  readonly suppliers: readonly string[];
  readonly hidden?: boolean;
}): JSX.Element {
  return (
    <ul
      className={`flex shrink-0 gap-3 pr-3 ${hidden ? 'marquee-dup' : ''}`}
      aria-hidden={hidden || undefined}
    >
      {suppliers.map((name) => (
        <li key={name} className="shrink-0">
          <span className="inline-flex items-center rounded-full border border-border bg-surface px-5 py-2.5 text-sm font-semibold text-foreground/80 shadow-sm">
            {name}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function SupplierChips({
  suppliers,
  centered = false,
  marquee = false,
}: SupplierChipsProps): JSX.Element | null {
  if (suppliers.length === 0) return null;

  if (marquee) {
    return (
      <div className="marquee w-full" data-testid="supplier-chips">
        <div className="marquee-track">
          <ChipRow suppliers={suppliers} />
          {/* The seam: an identical second copy, invisible to the reader. */}
          <ChipRow suppliers={suppliers} hidden />
        </div>
      </div>
    );
  }

  return (
    <ul
      className={`flex flex-wrap gap-2 ${centered ? 'justify-center' : ''}`}
      data-testid="supplier-chips"
    >
      {suppliers.map((name) => (
        <li key={name}>
          <Badge variant="default">{name}</Badge>
        </li>
      ))}
    </ul>
  );
}
